import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { SWISS_POSTAL_CODES } from "@/lib/swissPostalCodes";
import { MapPin } from "lucide-react";

function stripAccents(s) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/**
 * Swiss postal code auto-complete. Filters on zip prefix or city (accent-insensitive).
 * Le menu de suggestions est rendu dans un portal (document.body) en position
 * fixe, afin d'échapper à tout conteneur `overflow-hidden` ou contexte
 * d'empilement (il passait auparavant derrière la section suivante).
 */
export default function PostalCodeInput({
  value,
  onChange,
  onSelect,
  placeholder = "Code postal ou ville",
  className = "",
  inputClassName = "",
  testId = "postal-input",
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const query = (value || "").trim();

  const suggestions = useMemo(() => {
    if (!query) return [];
    const q = stripAccents(query);
    const isDigits = /^\d+$/.test(q);
    const out = [];
    for (const [zip, city] of SWISS_POSTAL_CODES) {
      const match = isDigits ? zip.startsWith(q) : stripAccents(city).includes(q) || zip.startsWith(q);
      if (match) {
        out.push([zip, city]);
        if (out.length >= 4) break;
      }
    }
    return out;
  }, [query]);

  const updateRect = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 6, left: r.left, width: r.width });
  }, []);

  // Positionne le menu et le garde aligné sur l'input (scroll / resize).
  useEffect(() => {
    if (!open) return;
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open, updateRect]);

  // Fermeture au clic extérieur (le menu étant en portal, on teste aussi menuRef).
  useEffect(() => {
    function onDown(e) {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inWrap && !inMenu) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function commit(zip, city) {
    onChange?.(zip);
    onSelect?.({ zip, city });
    setOpen(false);
    setActive(0);
  }

  function handleKey(e) {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const [z, c] = suggestions[active];
      commit(z, c);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const showMenu = open && suggestions.length > 0 && rect;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => {
          onChange?.(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        autoComplete="off"
        autoFocus={autoFocus}
        className={inputClassName}
        data-testid={testId}
      />
      {showMenu && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }}
          className="rounded-2xl bg-white border border-gray-100 shadow-[0_20px_50px_-15px_rgba(10,15,44,0.25)] overflow-hidden"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map(([zip, city], i) => (
            <button
              key={zip + city}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => commit(zip, city)}
              className={`w-full flex items-center gap-3 text-left px-4 py-2.5 transition-colors ${
                i === active ? "bg-[#EAF0FF]" : "bg-white"
              }`}
              data-testid={`${testId}-option-${i}`}
            >
              <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 ${i === active ? "bg-[#2D5BFF] text-white" : "bg-[#F7F7F8] text-[#0A0F2C]"}`}>
                <MapPin size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#0A0F2C] tabular-nums">{zip}</div>
                <div className="text-xs text-[#4B5563] truncate">{city}</div>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
